/* eslint-disable no-unused-vars */
import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store';
import {
  MainView, Home, Login, ProfileView,
  PublicView, AppLogin, CacheView,
  SecurityView,
  CallToMeView,
  TrustedThirdPartyAuthView,
  PasswordResetView,
  UtilsView,
  ShortenerView,
  ConverterView,
  GenerateqrView,
} from '@/views';

import ContextGetter from './context-getter';
import BackboneRoutes from './backbone-routes';
import MyRequestsRoutes from './my-requests';
import NotificationsRoutes from './notifications';
import CatalogFormRoutes from './catalog-forms';
import KnowledgeItemsRoutes from './knowlege-items';
import GlobalSearchRoutes from './global-search';
import AnnouncementAppRoutes from './announcementApp-routes';
import PasswordResetRoutes from './password-reset';
import NewsRoutes from './news';
import PulsoDigitalRoute from './pulsodigital';
import ConfigurationRoute from './configuration';

Vue.use(VueRouter);

const redirectIfSignedIn = (route, query) => function redirect() {
  if (this.$store != null && this.$store.getters['session/isSignedIn']) {
    this.$router.push({ path: route, query });
  }
};

const controlIosRedirect = (redirect) => function beforeEnter(to, from, next) {
  const sessionContact = store.getters['session/user'];
  if (sessionContact != null
    && sessionContact.hasIosUser()) {
    next(redirect);
  } else {
    next();
  }
};

const routes = [
  {
    path: '/',
    component: MainView,
    children: [
      /* {
        path: '/about',
        name: 'About',
        // route level code-splitting
        // this generates a  separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "about"  '@/views/About.vue'),
      }, */
      {
        path: '/profile/public/passwordreset',
        name: 'passwordreset',
        component: PasswordResetView,
        props: () => ({
          onMounted: redirectIfSignedIn('/'),
        }),
      },
      {
        path: '/profile/edit',
        component: ProfileView,
        beforeEnter: controlIosRedirect(''),
      },
      {
        path: '/utils',
        component: UtilsView,
      },
      {
        path: '/utils/shortener',
        component: ShortenerView,
      },
      {
        path: '/utils/converter',
        component: ConverterView,
      },
      {
        path: '/utils/generateqr',
        component: GenerateqrView,
      },
      {
        path: '/cache',
        component: CacheView,
      },
      {
        path: '/security',
        component: SecurityView,
      },
      {
        path: '/calltome',
        component: CallToMeView,
      },
      {
        path: '',
        component: Home,
      },
      {
        path: '/home',
        component: Home,
      },
    ].concat([
      MyRequestsRoutes.privatePaths,
      BackboneRoutes.privatePaths,
      CatalogFormRoutes.privatePaths,
      NotificationsRoutes.privatePaths,
      KnowledgeItemsRoutes.privatePaths,
      GlobalSearchRoutes.privatePaths,
      NewsRoutes.privatePaths,
      PulsoDigitalRoute.privatePaths,
      ConfigurationRoute.privatePaths,
    ].reduce((acum, item) => acum.concat(item))),
  },
  {
    path: '/login/:bopsid?',
    name: 'Login',
    component: Login,
    props: ({ query, params }) => ({
      message: params.message,
      redirect: query.redirect || params.redirect || '/',
      bopsid: params.bopsid !== '' ? params.bopsid : undefined,
    }),
    meta: {
      public: true,
    },
  },
  {
    path: '/login/onlyAccessPulso/:bopsid?',
    name: 'Login',
    component: Login,
    props: ({ query, params }) => ({
      message: params.message,
      redirect: '/pulsodigital',
      bopsid: params.bopsid !== '' ? params.bopsid : undefined,
      onlyAccessPulso: true,
    }),
    meta: {
      public: true,
    },
  },
  {
    path: '/app-login',
    name: 'AppLogin',
    component: AppLogin,
    props: ({ query }) => ({
      accessKey: query.accesskey,
      restAccessId: query.restaccess,
      jwtToken: query.token,
      redirect: query.redirect || '/',
      deviceOS: query.deviceOS,
    }),
    meta: {
      public: true,
    },
  },
  {
    path: '/ttp/:tempCode?',
    name: 'TrustedThirdPartyAuth',
    component: TrustedThirdPartyAuthView,
    props: ({ params }) => ({
      tempCode: params.tempCode,
    }),
    meta: {
      public: true,
    },
  },
  {
    path: '/logout',
    name: 'Logout',
    component: {
      async render() {
        this.$store.dispatch('session/logout')
          .then(() => {
            const movil = this.$store.getters['globalState/get']('deviceOS');
            if (movil) {
              this.$router.push({ name: 'Login' });
            } else {
              this.$router.push({ name: 'AnnouncementApp' });
            }
          }) // forcing reload to let router redirect to login
          .catch((err) => console.error(err));
      },
    },
  },
  {
    path: '/public',
    name: 'PublicView',
    component: PublicView,
    meta: {
      public: true,
    },
    children: [
      MyRequestsRoutes.publicPaths,
      BackboneRoutes.publicPaths,
      CatalogFormRoutes.publicPaths,
      NotificationsRoutes.publicPaths,
      KnowledgeItemsRoutes.publicPaths,
      GlobalSearchRoutes.publicPaths,
      AnnouncementAppRoutes.publicPaths,
      PasswordResetRoutes.publicPaths,
      NewsRoutes.publicPaths,
      PulsoDigitalRoute.publicPaths,
      ConfigurationRoute.publicPaths,
    ].reduce((acum, item) => {
      const makeRoutePublic = (r) => ({
        ...r,
        meta: {
          ...(r.meta || {}),
          public: true,
        },
        children: (r.children || []).map((c) => makeRoutePublic(c)),
      });

      return acum.concat(item.map((route) => makeRoutePublic(route)));
    }),
  },
  {
    path: '*',
    meta: { public: true },
    beforeEnter: async (to, from, next) => {
      try {
        if (!store.getters['session/checked']) {
          await store.dispatch('session/check');
        }
      } catch (e) {
        console.error('[router catch-all] session check failed:', e);
      }

      const isSignedIn = store.getters['session/isSignedIn'];
      if (isSignedIn) {
        if (to.path === '/') return next();
        return next({
          path: '/',
          query: {
            pathError: true,
            original: from && from.fullPath ? from.fullPath : to.fullPath,
          },
          replace: true,
        });
      }

      if (to.name === 'Login') return next();
      return next({
        name: 'Login',
        query: {
          pathError: true,
          redirect: to.fullPath,
        },
        replace: true,
      });
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: ContextGetter.getContext(),
  routes,
});

window.vueRouter = router;

function parseHashQuery(hash) {
  const pathHash = hash.startsWith('#') ? hash.slice(1) : hash;
  const [path, queryString] = pathHash.split('?', 2);
  const query = Object.fromEntries(new URLSearchParams(queryString || ''));
  return { query };
}

// session control
router.beforeEach(async (to, from, next) => {
  localStorage.setItem('from', from.path);
  const url = window.location.href;
  const intranet = 'junta-andalucia.es';
  if (!to.hash) {
    store.dispatch('routeState/setCurrentPath', '');
    store.dispatch('routeState/setCurrentPathId', '');
    store.dispatch('routeState/setNavInternal', false);
    store.dispatch('routeState/setQueryInternalParams', '');
    const toGenericForm = to.path === `/element/r/${ process.env.VUE_APP_GENERAL_FORM }`;
    const accessFormGeneric = to.params.isPermision;
    const toPublicPath = to.matched.some((record) => record.meta.public);

    const $next = (path, redirect) => {
      if (!path) {
        const onlyAccessPulso = localStorage.getItem('onlyAccessPulso') ? localStorage.getItem('onlyAccessPulso') : undefined;
        if (onlyAccessPulso && from.path.startsWith('/login/onlyAccessPulso') && to.path === '/pulsodigital') {
          if (url.includes(intranet)) {
            next();
          }
        } else if (onlyAccessPulso) {
          next();
        } else {
          next();
        }
      } else {
        const ob = { path };
        if (redirect && redirect !== '/') {
          ob.query = { redirect };
        }
        next(ob);
      }
    };

    if (
      !store.getters['session/checked']
      && !to.fullPath.includes('/app-login')
      && !accessFormGeneric
      && !toGenericForm
    ) {
      try {
        const sessionData = await store.dispatch('session/check');
        const user = store.getters['session/user'];
        // console.log('session checked', { sessionData, user, to });
        if (user?.accessType?.name === 'Accenture UI Provider PIN'
          || user?.accessType?.name === 'Accenture UI Provider DMSAS') {
          if (to.fullPath !== '/login') {
            this.$router.push('/login');
          }
          // TODO Cerrar Session y añadir mensaje
          // sessionData
        } else if (sessionData || toPublicPath) {
          $next();
        } else {
          $next('/login', to.fullPath);
        }
      } catch (error) {
        // Cuando hay un error de caducidad de sesion
        // Comprobamos si es ruta publica para si redirigir al siguiente url
        console.error('error checking session', error);
        if (!to.fullPath.includes('/public/announcementapp')) {
          $next('/login', to.fullPath);
        }
        $next('/login');
      }
    } else if (accessFormGeneric) {
      $next();
    } else if (toGenericForm && !accessFormGeneric) {
      $next(from.path);
    } else if (toPublicPath || store.getters['session/isSignedIn']) {
      $next();
    } else {
      $next('/login', to.fullPath);
    }
  } else {
    const { query } = parseHashQuery(to.hash);
    store.dispatch('routeState/setCurrentPath', to.hash.slice(1, to.hash.indexOf('/')));
    store.dispatch('routeState/setCurrentPathId', to.hash.slice(to.hash.indexOf('/') + 1));
    store.dispatch('routeState/setNavInternal', true);
    store.dispatch('routeState/setInternalQueryParams', query);
    if (window.innerWidth <= 1263) {
      document.querySelector('.messenger-header-options').querySelectorAll('span')[2].click();
    }
  }
});

router.afterEach(async (to, from) => {
  if (to.path === '/home') {
    router.push({ path: '/' });
  }
});

router.enableSessionControl = (eventBus) => {
  eventBus.on('api:unauthorized', () => {
    console.log('api:unauthorized event received!');
    const { currentRoute } = router;
    if (currentRoute.name !== 'Login') {
      let redirect;
      if (currentRoute.fullPath !== '/login' || currentRoute.query.redirect !== '/login') {
        redirect = currentRoute.query.redirect || currentRoute.fullPath;
      }
      const movil = store.getters['globalState/get']('deviceOS');
      if (movil) {
        router.push({
          name: 'Login',
          params: {
            redirect,
            message: 'sessionCaduced',
          },
        }).catch(() => {});
      } else {
        store.dispatch('session/logout');
        router.push({
          name: 'AnnouncementApp',
          params: {
            redirect,
          },
        }).catch(() => {});
      }
    }
  });
};

export default router;
